import type { Product } from './types';

export function isProductParent(product: Product): boolean {
  return product.parentId == null || product.parentId === '';
}

export function getFamilyParentId(product: Product): string {
  return isProductParent(product) ? product.id : (product.parentId as string);
}

/** Parent + colour children for the current family. */
export function getColorFamily(product: Product, all: Product[]): Product[] {
  const parentId = getFamilyParentId(product);
  const parent = all.find((p) => p.id === parentId);
  const children = all.filter((p) => p.parentId === parentId);
  const family = parent ? [parent, ...children] : children;

  // Legacy fallback while other SKUs still use colorVariantIds.
  if (family.length <= 1 && (product.colorVariantIds?.length ?? 0) > 1) {
    return (product.colorVariantIds ?? []).map((id) => all.find((p) => p.id === id)).filter((p): p is Product => !!p);
  }

  return family;
}

export function getConnectionCounterpart(product: Product, all: Product[]): Product | undefined {
  if (product.connectionVariantId) {
    return all.find((p) => p.id === product.connectionVariantId);
  }

  // Legacy peer fields
  if (product.wired && product.wirelessVariantId) {
    return all.find((p) => p.id === product.wirelessVariantId);
  }
  if (product.wireless && product.wiredVariantId) {
    return all.find((p) => p.id === product.wiredVariantId);
  }

  return undefined;
}

/** Deep-ish merge: child overrides parent; arrays/objects on child replace entirely when set. */
export function resolveInheritedProduct(product: Product, byId: Map<string, Product>): Product {
  if (isProductParent(product) || !product.parentId) {
    return product;
  }
  const parent = byId.get(product.parentId);
  if (!parent) {
    return product;
  }

  const resolvedParent = resolveInheritedProduct(parent, byId);

  return {
    ...resolvedParent,
    ...product,
    parentId: product.parentId,
    // Keep child's explicit connection / compatibility / media when present
    specs: product.specs ?? resolvedParent.specs,
    features: product.features ?? resolvedParent.features,
    warning: product.warning ?? resolvedParent.warning,
    installation: product.installation ?? resolvedParent.installation,
    downloads: product.downloads ?? resolvedParent.downloads,
    // Language lists follow whichever Czech list the child ends up with, never mixing child and parent.
    downloadsByLanguage: product.downloads ? product.downloadsByLanguage : resolvedParent.downloadsByLanguage,
    thumbnails: product.thumbnails ?? resolvedParent.thumbnails,
    compatibleWith: product.compatibleWith ?? resolvedParent.compatibleWith,
    installLocation: product.installLocation ?? resolvedParent.installLocation,
    searchAliases: product.searchAliases ?? resolvedParent.searchAliases,
    subcategoryIds: product.subcategoryIds?.length ? product.subcategoryIds : resolvedParent.subcategoryIds,
    filterLabels: product.filterLabels ?? resolvedParent.filterLabels,
    filterItemLabels: product.filterItemLabels ?? resolvedParent.filterItemLabels,
    filterChildLabels: product.filterChildLabels ?? resolvedParent.filterChildLabels,
  };
}
